import React, { useEffect, useMemo } from "react";

import { Formik, useFormikContext } from "formik";
import { t } from "i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Yup from "yup";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import CompaniesService from "@/services/CompaniesService";
import InvoicesService from "@/services/InvoicesService";

import { PageLayout } from "@/components/templates";

import { companyKeys, invoiceKeys } from "@/constants/CQueryKeys";

import {
	TFinalizeInvoiceRequest,
	TInvoiceDetail,
	TInvoiceFormValues,
	TInvoiceLine,
	TInvoicePrefill,
	TSaveInvoiceRequest,
} from "@/types";

import { promiseToast } from "@/modules/index";
import { dateToApi, dueDateFor, isBlankLine } from "@/utils";

import { InvoiceActionsBar } from "./components/InvoiceActionsBar";
import { InvoiceFormContext } from "./components/InvoiceFormContext";
import { InvoiceHeaderCard } from "./components/InvoiceHeaderCard";
import { InvoiceLinesEditor } from "./components/InvoiceLinesEditor";
import { InvoiceNotesCard } from "./components/InvoiceNotesCard";
import { InvoiceTotals } from "./components/InvoiceTotals";

const stripComputed = (
	lines: (TInvoiceLine & Record<string, unknown>)[]
): TInvoiceLine[] =>
	lines.map(
		({
			label,
			description,
			quantity,
			unit,
			unitPrice,
			vatApplicable,
			vatRate,
		}) => ({
			label,
			description: description ?? "",
			quantity,
			unit,
			unitPrice,
			vatApplicable,
			vatRate: vatRate ?? null,
		})
	);

const blankValues = (): TInvoiceFormValues => ({
	company: null,
	client: null,
	reportId: null,
	currency: "EUR",
	issuedAt: dateToApi(new Date()),
	dueAt: "",
	lines: [],
	notes: "",
});

const valuesFromInvoice = (invoice: TInvoiceDetail): TInvoiceFormValues => ({
	company: invoice.company,
	client: invoice.client,
	reportId: invoice.reportId,
	currency: invoice.currency,
	issuedAt: invoice.issuedAt ?? "",
	dueAt: invoice.dueAt ?? "",
	lines: stripComputed(invoice.lines),
	notes: invoice.notes ?? "",
});

const valuesFromPrefill = (prefill: TInvoicePrefill): TInvoiceFormValues => ({
	company: prefill.company,
	client: prefill.client,
	reportId: prefill.reportId,
	currency: prefill.currency,
	issuedAt: prefill.issuedAt,
	dueAt: prefill.dueAt,
	lines: stripComputed(prefill.lines),
	notes: "",
});

/** The API body: cards back to ids, blank lines dropped. */
export const toSaveRequest = (
	values: TInvoiceFormValues
): TSaveInvoiceRequest => ({
	companyId: values.company!.id,
	clientId: values.client?.id ?? null,
	reportId: values.reportId,
	currency: values.currency,
	issuedAt: values.issuedAt || null,
	dueAt: values.dueAt || null,
	lines: values.lines.filter((line) => !isBlankLine(line)),
	notes: values.notes.trim() || null,
});

const validationSchema = () =>
	Yup.object({
		company: Yup.object()
			.nullable()
			.required(t("invoices.form.companyRequired")),
		lines: Yup.array().min(1, t("invoices.form.linesRequired")),
	});

interface IInvoiceFormProps {
	invoiceId?: number;
	existing?: TInvoiceDetail;
	prefill?: TInvoicePrefill;
	onSaved: (invoice: TInvoiceDetail) => void;
}

/** Inside Formik: the cards, the totals and the actions; owns the mutations. */
const InvoiceForm: React.FC<IInvoiceFormProps> = ({
	invoiceId,
	existing,
	prefill,
	onSaved,
}) => {
	const { values, setFieldValue, validateForm, setTouched } =
		useFormikContext<TInvoiceFormValues>();
	const queryClient = useQueryClient();
	const companyId = values.company?.id;
	const isLocked = !!existing && !existing.isDraft;

	const { data: billing } = useQuery({
		queryKey: companyKeys.billing(companyId),
		queryFn: () => CompaniesService.fetchBilling(companyId!),
		enabled: !!companyId,
	});

	// A blank invoice takes the issuer's defaults when the issuer is
	// picked; one from a report or a saved one already has its own.
	useEffect(() => {
		if (!billing || invoiceId || prefill || isLocked) return;
		setFieldValue("currency", billing.defaultCurrency);
		if (values.issuedAt && !values.dueAt) {
			setFieldValue(
				"dueAt",
				dueDateFor(values.issuedAt, billing.paymentTermsDays)
			);
		}
		// Keyed on the issuer only: `values.*` change on every keystroke
		// and would overwrite what the user typed with the defaults.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [billing?.companyId]);

	const invalidate = (id: number) =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
			queryClient.invalidateQueries({ queryKey: invoiceKeys.summary }),
			queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) }),
		]);

	// Create or update, from the form as it stands. Finalize saves first
	// so the number lands on what the user sees.
	const persist = async (): Promise<TInvoiceDetail> => {
		const errors = await validateForm();
		if (Object.keys(errors).length > 0) {
			setTouched({ company: true, lines: true } as never, false);
			throw new Error(
				String(Object.values(errors)[0] ?? t("errors.generic"))
			);
		}
		const body = toSaveRequest(values);
		const saved = invoiceId
			? await InvoicesService.update(invoiceId, body)
			: await InvoicesService.create(body);
		await invalidate(saved.id);
		return saved;
	};

	const saveMutation = useMutation({
		mutationFn: persist,
		onSuccess: onSaved,
	});

	const finalizeMutation = useMutation({
		mutationFn: async (finalizeValues: TFinalizeInvoiceRequest) => {
			const saved = await persist();
			const finalized = await InvoicesService.finalize(
				saved.id,
				finalizeValues
			);
			await invalidate(finalized.id);
			return finalized;
		},
		onSuccess: onSaved,
	});

	const paidMutation = useMutation({
		mutationFn: (paid: boolean) =>
			InvoicesService.setPaid(existing!.id, paid),
		onSuccess: async (invoice) => {
			await invalidate(invoice.id);
			onSaved(invoice);
		},
	});

	const isBusy =
		saveMutation.isPending ||
		finalizeMutation.isPending ||
		paidMutation.isPending;

	return (
		<InvoiceFormContext.Provider
			value={{ invoiceId, existing, isLocked, billing, isBusy }}
		>
			<div className="flex flex-col gap-6">
				<InvoiceActionsBar
					onSaveDraft={() => promiseToast(saveMutation.mutateAsync())}
					onFinalize={(finalizeValues) =>
						finalizeMutation.mutateAsync(finalizeValues)
					}
					onSetPaid={(paid) => paidMutation.mutateAsync(paid)}
					isSaving={saveMutation.isPending}
					isFinalizing={finalizeMutation.isPending}
				/>
				<InvoiceHeaderCard />
				<InvoiceLinesEditor />
				<InvoiceTotals />
				<InvoiceNotesCard />
			</div>
		</InvoiceFormContext.Provider>
	);
};

export const InvoiceEditView: React.FC = () => {
	const { id } = useParams<{ id?: string }>();
	const invoiceId = id ? parseInt(id, 10) : undefined;
	const [searchParams] = useSearchParams();
	const reportId = Number(searchParams.get("reportId")) || undefined;
	const navigate = useNavigate();

	const {
		data: existing,
		isLoading: isInvoiceLoading,
		error: invoiceError,
	} = useQuery({
		queryKey: invoiceKeys.detail(invoiceId),
		queryFn: () => InvoicesService.get(invoiceId!),
		enabled: !!invoiceId,
	});

	const {
		data: prefill,
		isLoading: isPrefillLoading,
		error: prefillError,
	} = useQuery({
		queryKey: invoiceKeys.prefill(reportId),
		queryFn: () => InvoicesService.prefill(reportId!),
		enabled: !invoiceId && !!reportId,
	});

	const initialValues = useMemo(
		() =>
			existing
				? valuesFromInvoice(existing)
				: prefill
					? valuesFromPrefill(prefill)
					: blankValues(),
		[existing, prefill]
	);

	const title = existing?.number
		? `${t("invoices.form.header")} ${existing.number}`
		: invoiceId
			? `${t("invoices.form.header")} — ${t("invoices.draft")}`
			: t("invoices.new");

	return (
		<PageLayout
			title={title}
			isLoading={isInvoiceLoading || isPrefillLoading}
			error={
				invoiceError
					? t("invoices.notFound")
					: prefillError
						? t("invoices.errors.reportUnusable")
						: false
			}
			onBack={() => navigate("/invoices")}
			onBackLabel={t("invoices.back")}
		>
			<div className="mb-6 max-w-5xl">
				<Formik
					enableReinitialize
					initialValues={initialValues}
					validationSchema={validationSchema()}
					onSubmit={() => {}}
				>
					<InvoiceForm
						invoiceId={invoiceId}
						existing={existing}
						prefill={prefill}
						onSaved={(invoice) => {
							if (invoice.id !== invoiceId) {
								navigate(`/invoices/${invoice.id}`, {
									replace: true,
								});
							}
						}}
					/>
				</Formik>
			</div>
		</PageLayout>
	);
};
